import React, { useContext, useEffect, useRef, type FC } from 'react';
import { Slide, Slider } from 'pure-react-carousel';
import { CarouselContext } from 'pure-react-carousel';

import type { IProductListItem } from 'api/types';
import { WiseProductItem } from 'components/containers';

import styles from 'theme/components/wise/WiseHomeSubelements/WiseHomeSubelements.module.scss';

export type WiseHomeSubelementsProductSliderProps = {
  products: IProductListItem[];
  itemsPerSlide: number;
  onSlideIndexChange?: (index: number) => void;
  onLoadMore?: () => void;
  hasMore: boolean;
};


export const WiseHomeSubelementsProductSlider: FC<WiseHomeSubelementsProductSliderProps> = ({
  products,
  itemsPerSlide,
  onSlideIndexChange,
  onLoadMore,
  hasMore
}) => {
  const carouselContext = useContext(CarouselContext);
  const lastTriggeredSlideRef = useRef(-1);
  const maxTriggeredSlideRef = useRef(-1);
  const previousLengthRef = useRef(0);
  const totalSlideCount = products.length;

  useEffect(() => {
    if (!carouselContext) return;

    if (products.length !== previousLengthRef.current) {
      if (products.length < previousLengthRef.current) {
        lastTriggeredSlideRef.current = -1;
        maxTriggeredSlideRef.current = -1;
      }
      previousLengthRef.current = products.length;
    }

    const onSlideChange = () => {
      const currentSlide = carouselContext.state.currentSlide;
      onSlideIndexChange?.(currentSlide);

      if (!onLoadMore || !hasMore) return;

      const totalSlides = totalSlideCount;
      const threshold = Math.max(0, totalSlides - itemsPerSlide - 2);

      if (
        currentSlide >= threshold &&
        currentSlide !== lastTriggeredSlideRef.current &&
        currentSlide > maxTriggeredSlideRef.current &&
        hasMore
      ) {
        lastTriggeredSlideRef.current = currentSlide;
        maxTriggeredSlideRef.current = currentSlide;
        onLoadMore();
      }
    };

    carouselContext.subscribe(onSlideChange);
    onSlideChange();

    return () => {
      carouselContext.unsubscribe(onSlideChange);
    };
  }, [
    carouselContext,
    products.length,
    totalSlideCount,
    itemsPerSlide,
    onLoadMore,
    onSlideIndexChange,
    hasMore
  ]);

  return (
    <Slider>
      {products.map((product, index) => (
        <Slide key={product.id} index={index} className={styles.carouselSlide}>
          <div className={styles.productWrapper}>
            <WiseProductItem product={product} />
          </div>
        </Slide>
      ))}
    </Slider>
  );
};
